import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { api, type BackendTask } from '@/lib/api';
import {
  EXCLUDED_HANDLERS_FROM_FILTERS,
  HANDLER_TYPE_FILTERS,
  TASK_STATUSES,
  backendToTaskUI,
  emptyTasksByStatus,
  periodToCutoffMs,
  type TaskFilters,
  type TaskStatus,
  type TaskUI,
  type TasksByStatus,
} from '../types';

/**
 * Tamanho do chunk pra fetch all-in-one. Usamos chunks paralelos pra cobrir
 * todas as linhas de um status sem segurar o initial paint por 1 query gigante.
 * 1000 mantém request body controlado mas reduz nº de roundtrips.
 */
const FETCH_CHUNK = 1000;

interface UseTasksBoardArgs {
  tenantId: string | null;
  filters: TaskFilters;
}

export interface UseTasksBoardReturn {
  /** Tasks filtradas (cache aplicado nos filtros) — completo, sem slice de visíveis. */
  byStatus: TasksByStatus;
  /** Counts pós-filtros — usado em KPI e header das colunas. */
  filteredTotals: Record<TaskStatus, number>;
  /** Counts agregados absolutos (DB authority). */
  totals: Record<TaskStatus, number>;
  /** True enquanto o fetch all-in-one está rodando. */
  isLoading: boolean;
  error: string | null;
  /** Lista de handlers vistos no cache (sem @echo, só @-prefixados). */
  knownHandlers: string[];
  /** target_slugs únicos vistos por handler. Pra dropdowns tipo. */
  targetsByHandler: Record<string, string[]>;
  refetch: () => void;
}

const ZERO_TOTALS: Record<TaskStatus, number> = {
  pending: 0,
  running: 0,
  done: 0,
  failed: 0,
  retry: 0,
  cancelled: 0,
  paused: 0,
};

function isViableTaskRow(row: unknown): row is BackendTask {
  if (!row || typeof row !== 'object') return false;
  const r = row as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id) return false;
  if (typeof r.status !== 'string' || !r.status) return false;
  if (typeof r.handler !== 'string') return false;
  return TASK_STATUSES.includes(r.status as TaskStatus);
}

export function useTasksBoard({ tenantId, filters }: UseTasksBoardArgs): UseTasksBoardReturn {
  const [byStatus, setByStatus] = useState<TasksByStatus>(emptyTasksByStatus);
  const [totals, setTotals] = useState<Record<TaskStatus, number>>(ZERO_TOTALS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const realtimeRef = useRef<RealtimeChannel | null>(null);
  const fetchVersionRef = useRef(0);

  /**
   * Fetch all-in-one: stats + todas as linhas de cada status em chunks
   * paralelos. Skeleton fica visível até TUDO chegar; quando termina, board
   * pinta completo de uma vez. Sem progressive render.
   */
  const fetchAll = useCallback(async () => {
    const myVersion = ++fetchVersionRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const stats = await api.getTaskStats();
      if (myVersion !== fetchVersionRef.current) return;

      const allLists = await Promise.all(
        TASK_STATUSES.map(async (status) => {
          const total = stats.counts[status] ?? 0;
          if (total === 0) return { status, items: [] as BackendTask[], total: 0 };

          const numChunks = Math.ceil(total / FETCH_CHUNK);
          const chunkPromises = Array.from({ length: numChunks }, (_, i) =>
            api
              .listTasks({ status, limit: FETCH_CHUNK, offset: i * FETCH_CHUNK })
              .then((r) => r.items)
              .catch(() => [] as BackendTask[]),
          );
          const chunks = await Promise.all(chunkPromises);
          return { status, items: chunks.flat(), total };
        }),
      );
      if (myVersion !== fetchVersionRef.current) return;

      const next = emptyTasksByStatus();
      const nextTotals: Record<TaskStatus, number> = { ...ZERO_TOTALS };
      for (const { status, items, total } of allLists) {
        next[status] = items.map(backendToTaskUI);
        nextTotals[status] = total;
      }

      setByStatus(next);
      setTotals(nextTotals);
      setIsLoading(false);
    } catch (err) {
      if (myVersion !== fetchVersionRef.current) return;
      setError(err instanceof Error ? err.message : 'Falha ao carregar tasks');
      setIsLoading(false);
    }
  }, []);

  // ── Realtime sync ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!tenantId) return;
    if (realtimeRef.current) supabase.removeChannel(realtimeRef.current);

    const ch = supabase
      .channel('tasks-board-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          const newRow = isViableTaskRow(payload.new) ? (payload.new as BackendTask) : null;
          const oldRowRaw = (payload.old ?? null) as Record<string, unknown> | null;

          if (payload.eventType === 'INSERT' && newRow) {
            const ui = backendToTaskUI(newRow);
            setByStatus((prev) => ({ ...prev, [ui.status]: [ui, ...prev[ui.status]] }));
            setTotals((prev) => ({ ...prev, [ui.status]: (prev[ui.status] ?? 0) + 1 }));
            return;
          }

          if (payload.eventType === 'UPDATE' && newRow) {
            const ui = backendToTaskUI(newRow);
            const oldStatusRaw = oldRowRaw?.status;
            const oldStatus =
              typeof oldStatusRaw === 'string' && (TASK_STATUSES as readonly string[]).includes(oldStatusRaw)
                ? (oldStatusRaw as TaskStatus)
                : ui.status;

            setByStatus((prev) => {
              if (oldStatus === ui.status) {
                return {
                  ...prev,
                  [ui.status]: prev[ui.status].map((t) => (t.id === ui.id ? ui : t)),
                };
              }
              return {
                ...prev,
                [oldStatus]: prev[oldStatus].filter((t) => t.id !== ui.id),
                [ui.status]: [ui, ...prev[ui.status]],
              };
            });

            if (oldStatus !== ui.status) {
              setTotals((prev) => ({
                ...prev,
                [oldStatus]: Math.max(0, (prev[oldStatus] ?? 0) - 1),
                [ui.status]: (prev[ui.status] ?? 0) + 1,
              }));
            }
            return;
          }

          if (payload.eventType === 'DELETE' && oldRowRaw) {
            const oldStatusRaw = oldRowRaw.status;
            const oldId = oldRowRaw.id;
            if (
              typeof oldId === 'string' &&
              typeof oldStatusRaw === 'string' &&
              (TASK_STATUSES as readonly string[]).includes(oldStatusRaw)
            ) {
              const status = oldStatusRaw as TaskStatus;
              setByStatus((prev) => ({ ...prev, [status]: prev[status].filter((t) => t.id !== oldId) }));
              setTotals((prev) => ({ ...prev, [status]: Math.max(0, (prev[status] ?? 0) - 1) }));
            }
            return;
          }
        },
      )
      .subscribe();

    realtimeRef.current = ch;
    return () => {
      if (realtimeRef.current) {
        supabase.removeChannel(realtimeRef.current);
        realtimeRef.current = null;
      }
    };
  }, [tenantId]);

  // ── Initial fetch quando tenant muda ─────────────────────────────────────
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) void fetchAll();
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId, fetchAll]);

  // ── Filtros aplicados client-side sobre cache ────────────────────────────
  const filtered = useMemo<TasksByStatus>(() => {
    const next = emptyTasksByStatus();
    const cutoffMs = periodToCutoffMs(filters.donePeriod);
    const q = filters.q.trim().toLowerCase();
    const handlerSet = new Set(filters.handler);
    const statusSet = new Set(filters.status);

    // Filtros tipo (byHandler) — task passa se há cláusula casando exatamente.
    // Múltiplas chaves em byHandler combinam com OR entre tipos.
    const byHandlerEntries = Object.entries(filters.byHandler).filter(([, v]) => v.length);

    function matchesByHandler(task: TaskUI): boolean {
      if (byHandlerEntries.length === 0) return true;
      return byHandlerEntries.some(
        ([handler, targets]) =>
          task.handler === handler && task.target_slug !== null && targets.includes(task.target_slug),
      );
    }

    /**
     * Status afetados pelo filtro temporal (donePeriod). Done usa completed_at;
     * issues (failed/retry/cancelled) usam updated_at (que captura o momento da
     * transição final pro estado, mesmo quando completed_at fica null).
     */
    const periodFiltered: TaskStatus[] = ['done', 'failed', 'retry', 'cancelled'];

    for (const status of TASK_STATUSES) {
      if (statusSet.size > 0 && !statusSet.has(status)) {
        next[status] = [];
        continue;
      }
      let arr = byStatus[status];
      if (cutoffMs !== null && periodFiltered.includes(status)) {
        arr = arr.filter((t) => {
          const ts = t.completed_at ?? t.updated_at;
          if (!ts) return false;
          return Date.parse(ts) >= cutoffMs;
        });
      }
      if (handlerSet.size > 0) {
        arr = arr.filter((t) => handlerSet.has(t.handler));
      }
      arr = arr.filter(matchesByHandler);
      // P1.13 F7 — Toggle pra esconder órfãs (sub-tasks com FK NULL).
      if (filters.hideOrphans) {
        arr = arr.filter((t) => !t.is_orphan);
      }
      if (q) {
        arr = arr.filter((t) => {
          const hay = `${t.title} ${t.handler} ${t.target_slug ?? ''} ${t.id}`.toLowerCase();
          return hay.includes(q);
        });
      }
      next[status] = arr;
    }
    return next;
  }, [byStatus, filters]);

  /**
   * Lista de handlers do cache TOTAL (não filtrado).
   * Filtra:
   *   1. Handlers excluídos (@echo).
   *   2. Strings que não seguem o padrão @kebab — handlers legacy/malformados
   *      que apareciam no dropdown poluindo a UX (ex: "Get Session Status").
   */
  const knownHandlers = useMemo(() => {
    const set = new Set<string>();
    for (const status of TASK_STATUSES) {
      for (const t of byStatus[status]) {
        if (!t.handler) continue;
        if (!t.handler.startsWith('@')) continue;
        if (EXCLUDED_HANDLERS_FROM_FILTERS.has(t.handler)) continue;
        set.add(t.handler);
      }
    }
    return [...set].sort();
  }, [byStatus]);

  /** Targets únicos por handler — vem do cache TOTAL (não filtrado). */
  const targetsByHandler = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const meta of HANDLER_TYPE_FILTERS) map[meta.handler] = new Set();
    for (const status of TASK_STATUSES) {
      for (const t of byStatus[status]) {
        if (!t.target_slug) continue;
        if (!map[t.handler]) continue;
        map[t.handler].add(t.target_slug);
      }
    }
    const out: Record<string, string[]> = {};
    for (const [handler, set] of Object.entries(map)) out[handler] = [...set].sort();
    return out;
  }, [byStatus]);

  /**
   * Counts pós-filtro — usado pelos KPIs e header das colunas.
   * Quando filtros ativos, mostra quanto bate; sem filtros, igual a `totals`.
   */
  const filteredTotals = useMemo<Record<TaskStatus, number>>(() => {
    const out = { ...ZERO_TOTALS };
    for (const s of TASK_STATUSES) out[s] = filtered[s].length;
    return out;
  }, [filtered]);

  const refetch = useCallback(() => {
    void fetchAll();
  }, [fetchAll]);

  return {
    byStatus: filtered,
    filteredTotals,
    totals,
    isLoading,
    error,
    knownHandlers,
    targetsByHandler,
    refetch,
  };
}
